import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { PrescriptionApi } from '../../../apis/PrescriptionApi/PrescriptionApi';
import { QueryKeys } from '../../../queryDefaults/QueryKeys';
import { AppMedError } from '../../../error/AppMedError';

function sanitizePart(input: string): string {
    return input.replace(/[/\\:*?"<>|]/g, '').replace(/\s+/g, '-');
}

function buildPdfFilename(params: { patientName?: string | null; patientDateOfBirth?: string | null }): string {
    const nameParts = (params.patientName || 'Unknown').trim().split(/\s+/);
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0];
    const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join('-') : 'Unknown';
    const dob = params.patientDateOfBirth ? params.patientDateOfBirth.split('T')[0] : 'Unknown';
    const pdfDate = new Date().toISOString().split('T')[0];
    const raw = `Prescriptions_${sanitizePart(lastName)}_${sanitizePart(firstName)}_${dob}_${pdfDate}.pdf`;
    return raw.length > 200 ? raw.substring(0, 196) + '.pdf' : raw;
}

interface DownloadPrescriptionsPdfParams {
    patientId: string;
    language: string;
    patientName?: string | null;
    patientDateOfBirth?: string | null;
}

export function useDownloadPrescriptionsPdf() {
    const { t } = useTranslation();

    return useMutation<Blob, AppMedError, DownloadPrescriptionsPdfParams>({
        mutationKey: [QueryKeys.prescription, 'downloadPdf'],
        mutationFn: async ({ patientId, language }) => {
            return await PrescriptionApi.downloadPrescriptionsPdf(patientId, language);
        },
        onSuccess: (blob, params) => {
            const filename = buildPdfFilename(params);
            PrescriptionApi.downloadPdfFile(blob, filename);

            notifications.show({
                title: t('prescriptionHistory.notifications.downloadPdfSuccess.title'),
                message: t('prescriptionHistory.notifications.downloadPdfSuccess.message'),
                color: 'green',
            });
        },
        onError: (error: AppMedError) => {
            notifications.show({
                title: t('prescriptionHistory.notifications.downloadPdfError.title'),
                message: error?.message || t('prescriptionHistory.notifications.downloadPdfError.message'),
                color: 'red',
            });
        },
    });
}
